import { Button, ButtonVariant, Icon } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { FulfilmentDetail } from '../../../../../models/fulfilment'
import { useSaleManager } from '../../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../../state/hooks'
import {
  selectSaleFulfilmentDetail,
  setFulfilmentDetails,
} from '../../../../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../../../../util/feature-cache'
import { FEATURE_ENABLE_LRX_33749_FIX_FE_CAPTURE_SHIPPING_ADDRESS_ISSUES } from '../../../../../constants/wr-features'
import { useSaleFulfilment } from '../hook/useSaleFulfilment'
import {
  HEAP_DATA_TRACK_ADD_ADDRESS,
  HEAP_DATA_TRACK_EDIT_ADDRESS,
} from '../../../sale-model.constant'
import { SaleAddressModal } from './SaleAddressModal'

export const EditAddressButtons = {
  edit: 'edit',
  add: 'add',
  addShipping: 'addShipping',
} as const

export type AddOrEditAddressButtonType = keyof typeof EditAddressButtons

interface EditAddressButtonProps {
  type: AddOrEditAddressButtonType
  isModalOpeningOnError?: boolean
  hasError?: boolean
  text?: ReactNode
  variant?: ButtonVariant
  onClick?: () => void
  onClose?: () => void
  onSave?: (fulfillmentDetails: FulfilmentDetail[]) => void
  fulfilmentDetail?: FulfilmentDetail
  lineItemLocalId?: string
}

export const AddOrEditAddressButton: React.FC<EditAddressButtonProps> = ({
  type,
  isModalOpeningOnError,
  hasError,
  text,
  variant,
  onClick,
  onClose,
  onSave,
  fulfilmentDetail,
  lineItemLocalId,
}: EditAddressButtonProps) => {
  const [showSaleAddressModal, setShowSaleAddressModal] = useState(false)
  const dispatch = useAppDispatch()
  const saleManager = useSaleManager()

  const isFeShippingAddressCaptureEnabled = getFeatureFromCache(
    FEATURE_ENABLE_LRX_33749_FIX_FE_CAPTURE_SHIPPING_ADDRESS_ISSUES
  )

  const savedFulfillmentDetails = useAppSelector(selectSaleFulfilmentDetail)
  const fulfillmentDetails = isFeShippingAddressCaptureEnabled
    ? fulfilmentDetail ?? savedFulfillmentDetails
    : savedFulfillmentDetails

  const openAddressModal = useCallback(() => {
    setShowSaleAddressModal(true)
  }, [])

  const { isDisabled } = useSaleFulfilment(lineItemLocalId || '')

  const closeAddressModal = (fulfillmentDetails?: FulfilmentDetail) => {
    setShowSaleAddressModal(false)
    if (fulfillmentDetails && !onSave) {
      dispatch(
        setFulfilmentDetails([
          { ...fulfillmentDetails!, shouldAddNewAddress: false },
        ])
      )
    }
    onClose?.()
  }
  const isAddType =
    type === EditAddressButtons.add || type === EditAddressButtons.addShipping
  const icon = type === EditAddressButtons.edit ? 'pencil' : 'plus'

  const classNameMH = isModalOpeningOnError
    ? isAddType
      ? 'vd-ml1'
      : 'vd-mr1'
    : ''
  const classNameMV = !isAddType && !text ? 'vd-mt5' : ''
  useEffect(() => {
    if (hasError && isModalOpeningOnError) {
      openAddressModal()
    }
  }, [hasError, isModalOpeningOnError, openAddressModal])

  const onSaveForm = (fulfilmentDetails: FulfilmentDetail[]) => {
    if (onSave && isFeShippingAddressCaptureEnabled) {
      onSave(fulfilmentDetails)
      return
    }

    dispatch(setFulfilmentDetails(fulfilmentDetails))
    saleManager.updateFulfillmentDetails(fulfilmentDetails)
  }

  return (
    <>
      <Button
        disabled={isDisabled}
        modifier={text ? undefined : 'text'}
        variant={variant || 'supplementary'}
        onClick={() => {
          onClick?.()
          openAddressModal()
        }}
        className={`${classNameMH} ${classNameMV}`}
        data-track={
          type === EditAddressButtons.edit
            ? HEAP_DATA_TRACK_EDIT_ADDRESS
            : HEAP_DATA_TRACK_ADD_ADDRESS
        }
      >
        {text || (
          <>
            <Icon icon={icon} className={isAddType ? 'vd-mr1' : ''} />
            {isAddType && <T _str="Add address" _context="Action link" />}
          </>
        )}
      </Button>

      {showSaleAddressModal && (
        <SaleAddressModal
          formType={type}
          fulfillmentDetails={fulfillmentDetails}
          onClose={closeAddressModal}
          onSave={onSaveForm}
        />
      )}
    </>
  )
}
